import { Dialog, DialogActions, DialogContent, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { ButtonCancel, ButtonConfirm } from 'components/buttons';
import { MprcDatePicker } from 'components/MprcDatePicker';
import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { ManagedUserCredentialsResponse, UserCredentialType, UserCredentialTypeSelectorResponse } from 'generated/CoreApi_v1';
import LibUtils from 'lib/LibUtils';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    record?: ManagedUserCredentialsResponse;
    userId: string;
    userName: string;
    credentialTypes: UserCredentialTypeSelectorResponse[];
    onClose: (record?: ManagedUserCredentialsResponse) => void;
}

interface IValidationFields {
    title?: string[] | null;
    credentialType?: string[] | null;
}

////////////////////////////////////////////////////////////////////////////

export const AddOrUpdateCredential: React.FC<IProps> = (props) => {

    const { record, userId, userName, credentialTypes, onClose } = props;
    const { t } = useTranslation();
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [credential, setCredential] = useState<ManagedUserCredentialsResponse>
        (record
            ? record
            : {
                credentialType: UserCredentialType.PositionsHeld,
                startedOn: null,
                finishedOn: null
            } as ManagedUserCredentialsResponse
        );

    const formValidation = useRef<IValidationFields>({
        title: undefined,
        credentialType: null,
    });

    const action = record == undefined ? "create" : "update";
    const canSave = Object.entries(formValidation.current).filter(([key, value]) => value !== null).length == 0 && recordChanged;

    ////////////////////////////////////////////////////////////////////////////

    ///
    const updateFormValidation = (record: ManagedUserCredentialsResponse) => {

        formValidation.current.title = (record.title == undefined || record.title.length === 0)
            ? [t("Messages.RequiredFieldText", { ns: "shared" })]
            : null;

        formValidation.current.credentialType = record.credentialType == undefined
            ? [t("Messages.RequiredFieldText", { ns: "shared" })]
            : null;
    };

    ///
    const updateRecord = (name: keyof ManagedUserCredentialsResponse, v: any) => {
        setCredential((state) => {

            const newState = { ...state!, [name]: v };
            updateFormValidation(newState);

            const isRecordChanged = !LibUtils.deepCompare(newState, LibUtils.deepCopy(record ?? {}),
                (key, value) => {
                    if (value === null) return "";
                    return value;
                }
            );
            setRecordChanged(isRecordChanged);
            return newState;
        });
    };

    ///
    const resolveErrors = (e: any | unknown) => {
        const r = MprcResolveRequestErrors(e);

        if (r.fieldsWithError != null) {
            Object.entries(r.fieldsWithError ?? {}).forEach(([key, value]) => {
                if (formValidation.current.hasOwnProperty(key)) {
                    (formValidation.current as any)[key] = value;
                }
            });

            setViewError(r.otherErrors);
            enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
        }
        else {
            setViewError(e);
        }
    };

    ///
    const save = () => {
        setSaving(true);
        api.v1.userManagement.addOrUpdateCredential({ ...credential, userId })
            .then(result => {
                const credentialId = result.data;

                if (credentialId != undefined) {
                    if (action == "create") {
                        enqueueSnackbar(t("User.Edit.Credentials.AddSuccessMessage"), { variant: "success" });
                        onClose({ ...credential, credentialId });
                    }
                    else {
                        enqueueSnackbar(t("User.Edit.Credentials.EditSuccessMessage"), { variant: "success" });
                        onClose(credential);
                    }
                } else {
                    enqueueSnackbar(t("User.Edit.Credentials.EditErrorMessage"), { variant: "error" });
                }
            })
            .catch(e => resolveErrors(e))
            .finally(() => setSaving(false));
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        updateFormValidation(credential);
    }, []);

    return (
        <>
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />
            <MprcRequestIndicator visible={saving} type={"post"} />
            <Dialog
                open
                fullWidth
                maxWidth="xs"
                onClose={() => onClose(undefined)}
            >
                <MprcDialogTitleClose
                    shrinkPadding
                    title={action == "create" ? t("User.Edit.Credentials.AddTitle") : t("User.Edit.Credentials.EditTitle")}
                    subtitle={userName}
                    onClose={() => onClose(undefined)}
                />
                <DialogContent>
                    <Grid container direction="column" spacing={1}>
                        <Grid>
                            <FormControl
                                fullWidth
                                required
                                error={formValidation.current.credentialType != undefined}
                            >
                                <InputLabel>{t("User.Edit.Credentials.CredencialTypeLabel")}</InputLabel>
                                <Select
                                    label={t("User.Edit.Credentials.CredencialTypeLabel")}
                                    value={credential.credentialType}
                                    onChange={(e) => updateRecord("credentialType", e.target.value as UserCredentialType)}
                                >
                                    {credentialTypes.map(item => <MenuItem key={item.type} value={item.type} >{item.name}</MenuItem>)}
                                </Select>
                                <FormHelperText error={formValidation.current.credentialType != undefined}>{formValidation.current.credentialType}</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid>
                            <TextField
                                fullWidth
                                label={t("User.Edit.Credentials.TitleLabel")}
                                value={credential.title}
                                onChange={e => updateRecord("title", e.target.value)}
                                error={formValidation.current.title != undefined}
                                helperText={formValidation.current.title}
                                required
                            />
                        </Grid>
                        <Grid>
                            <TextField
                                fullWidth
                                label={t("User.Edit.Credentials.DescriptionLabel")}
                                multiline
                                minRows={3}
                                value={credential.description}
                                onChange={e => updateRecord("description", e.target.value)}
                            />
                        </Grid>
                        <Grid sx={{ mt: 2 }}>
                            <Grid container direction="row" spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <MprcDatePicker
                                        placeholder={t("shared:Literal.From")}
                                        onChange={(date) => updateRecord("startedOn", date)}
                                        value={credential.startedOn}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <MprcDatePicker
                                        placeholder={t("shared:Literal.Until")}
                                        onChange={(date) => updateRecord("finishedOn", date)}
                                        value={credential.finishedOn}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonCancel variant="contained" onClick={() => onClose(undefined)} />
                    <ButtonConfirm
                        variant="contained"
                        disabled={!canSave}
                        onClick={() => save()}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
